import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeConfig } from '@/types';
import { themes, type ThemeName } from '@/lib/themes';

import { ThemeContext } from './theme-context';

interface ThemeProviderProps {
  children: ReactNode;
}

function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeName, setThemeNameState] = useState<ThemeName>('Solitude');

  useEffect(() => {
    const persisted = localStorage.getItem('theme-vibe');
    if (persisted && Object.keys(themes).includes(persisted)) {
      setThemeNameState(persisted as ThemeName);
    }
  }, []);

  const setThemeName = (newThemeName: ThemeName) => {
    localStorage.setItem('theme-vibe', newThemeName);
    setThemeNameState(newThemeName);
  };

  // Each theme declares the single mode it's designed for (Solitude: dark,
  // Solitude Light: light) — there's no independent light/dark toggle.
  const activeMode = themes[themeName].defaultMode;

  // Pick one background photo per visit for themes that have them (e.g. Solitude).
  const backgroundImage = useMemo(() => {
    const images = themes[themeName]?.backgroundImages;
    return images && images.length > 0 ? pickRandom(images) : null;
  }, [themeName]);

  const themeConfig: ThemeConfig = {
    primaryColor: themes[themeName].light.primaryColor,
    secondaryColor: themes[themeName].light.secondaryColor,
    accentColor: themes[themeName].light.accentColor,
    backgroundColor: themes[themeName].light.backgroundColor,
    textColor: themes[themeName].light.textColor,
    backgroundGradient: themes[themeName].light.backgroundGradient,
    animationIntensity: 'subtle',
  };

  // Apply CSS custom properties when the theme changes
  useEffect(() => {
    const root = document.documentElement;

    if (activeMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    const palette = themes[themeName][activeMode];

    root.style.setProperty('--color-primary', palette.primaryColor);
    root.style.setProperty('--color-secondary', palette.secondaryColor);
    root.style.setProperty('--color-accent', palette.accentColor);
    root.style.setProperty('--color-background', palette.backgroundColor);
    root.style.setProperty('--color-text', palette.textColor);

    // Themes with photo backgrounds get a scrim tinted to the active
    // palette's background color (dark scrim in dark mode, light scrim
    // in light mode) instead of the flat gradient.
    const background = backgroundImage
      ? `linear-gradient(rgba(${hexToRgb(palette.backgroundColor)}, 0.72), rgba(${hexToRgb(palette.backgroundColor)}, 0.85)), url('${backgroundImage}') center / cover fixed no-repeat`
      : palette.backgroundGradient;

    root.style.setProperty('--gradient-background', background);
    root.style.setProperty('--animation-intensity', '0.5');
  }, [themeName, activeMode, backgroundImage]);

  return (
    <ThemeContext.Provider value={{ theme: themeConfig, isLoading: false, themeName, setThemeName }}>
      {children}
    </ThemeContext.Provider>
  );
}

function hexToRgb(hex: string): string {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}
